/**
 * Per-system model cache (M3 plan R2, §2.6.3). Deferred models (the `reach.` prefix, kept out of boot by the
 * `include` filter on `loadAllAssets`) load here when a system is entered, so they never weigh on the first
 * load. Satisfies `ModelSource` and `StructureColliderSource` from `src/reach/runtime/types.ts` structurally.
 *
 * - `ensure(ids)` loads what is missing, shares one in-flight load per id between concurrent callers, and
 *   rejects with an `AssetLoadError` naming the failing id (an unknown id fails the same way). A failed id
 *   is forgotten, so a later `ensure` retries it.
 * - `get`, `colliderOf` and `socketsOf` never load. Sockets come from the manifest entry, so they are known
 *   before the model is; the collider needs the loaded GLB, except a `ball`, which the entry fully describes.
 * - Models handed in as `preloaded` (the boot set) are served as-is and never reloaded.
 */
import type { AssetsFile, ModelEntry, SocketEntry } from '../data/assetSchema'
import { AssetLoadError } from './boot'
import type { AssetLoader, ColliderData, LoadedModel } from './loader'

export type ModelLoadSource = Pick<AssetLoader, 'loadModel'>

export interface ModelCache {
  /** Resolves once every id is loaded (or preloaded); rejects with an `AssetLoadError` for the first failure. */
  ensure(ids: readonly string[]): Promise<void>
  get(id: string): LoadedModel | undefined
  /** The loaded model's collider; a `ball` straight from the entry; otherwise null (not loaded, or none). */
  colliderOf(id: string): ColliderData | null
  /** Socket poses from the manifest entry (no load needed); empty for an unknown id. */
  socketsOf(id: string): readonly SocketEntry[]
  /** Ids this cache has loaded itself (not the preloaded ones), sorted. */
  loadedIds(): string[]
  /** Forgets every loaded model; loads still in flight are dropped when they land. */
  dispose(): void
}

const NO_SOCKETS: readonly SocketEntry[] = Object.freeze([])

export function createModelCache(
  loader: ModelLoadSource,
  assets: AssetsFile,
  preloaded: ReadonlyMap<string, LoadedModel> = new Map(),
): ModelCache {
  const entries = new Map<string, ModelEntry>()
  for (const e of assets.models) entries.set(e.id, e)
  const loaded = new Map<string, LoadedModel>()
  const inFlight = new Map<string, Promise<LoadedModel>>()
  let generation = 0

  const load = (id: string): Promise<LoadedModel> => {
    const pending = inFlight.get(id)
    if (pending) return pending
    const entry = entries.get(id)
    const gen = generation
    const run = async (): Promise<LoadedModel> => {
      if (!entry) throw new AssetLoadError(id, new Error('not in assets.json'))
      try {
        const model = await loader.loadModel(entry)
        if (gen === generation) loaded.set(id, model)
        return model
      } catch (err: unknown) {
        throw new AssetLoadError(id, err)
      }
    }
    const promise = run()
    inFlight.set(id, promise)
    const settle = (): void => {
      if (inFlight.get(id) === promise) inFlight.delete(id)
    }
    promise.then(settle, settle)
    return promise
  }

  return {
    async ensure(ids: readonly string[]): Promise<void> {
      const waits: Promise<LoadedModel>[] = []
      for (const id of new Set(ids)) {
        if (preloaded.has(id) || loaded.has(id)) continue
        waits.push(load(id))
      }
      await Promise.all(waits)
    },
    get(id: string): LoadedModel | undefined {
      return loaded.get(id) ?? preloaded.get(id)
    },
    colliderOf(id: string): ColliderData | null {
      const model = loaded.get(id) ?? preloaded.get(id)
      if (model) return model.collider
      const collider = entries.get(id)?.collider
      return collider?.kind === 'ball' ? { kind: 'ball', radius: collider.radius } : null
    },
    socketsOf(id: string): readonly SocketEntry[] {
      return entries.get(id)?.sockets ?? NO_SOCKETS
    },
    loadedIds(): string[] {
      return [...loaded.keys()].sort()
    },
    dispose(): void {
      generation++
      loaded.clear()
      inFlight.clear()
    },
  }
}
